import { Map as ImmutableMap } from 'immutable';
import { useLocation } from 'react-router-dom';
import { getEdgeIdFromGoToActionId, getPreviousActionId } from 'automation-ui-action-features/records/recordsUtils';
import { useCallback, useState, useMemo } from 'react';
import memoize from 'transmute/memoize';
import { translateToGraph } from 'automation-ui-canvas/utils/platformWorkflowTranslators';
import * as SpecialActionIds from 'automation-ui-flow-core/canvas/SpecialActionIds';
const useGetGoToActionFlowData = ({
  flow
}) => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const edgeId = searchParams.get('edgeId');
  const newAction = searchParams.get('newAction');
  const prevAction = edgeId ? getPreviousActionId(edgeId) : null;
  const prevActionId = edgeId ? edgeId.split('-edge-')[0] : null;
  const correctedEdgeId = edgeId ? getEdgeIdFromGoToActionId(edgeId) : null;
  const isNewAction = newAction === 'true' ? SpecialActionIds.NEW_ACTION : false;
  const getActionIdsForGoToAction = useCallback(flowAsGraph => memoize(previousActionId => {
    return flowAsGraph.getNodeIdsForGoToNode(previousActionId, edgeId);
  }, previousActionId => `${previousActionId}-${edgeId}`), [edgeId]);
  const flowAsGraph = translateToGraph(flow, true);
  const allEdges = flowAsGraph.getEdges();
  const [goToActionIds, matchingGoToActionId] = useMemo(() => {
    let goToActionCounter = 0;
    const ids = [];
    let matchingId = null;
    const sortedEdges = [...allEdges].sort((edgeA, edgeB) => {
      var _edgeA$getId, _edgeA$getId2, _edgeB$getId, _edgeB$getId2;
      return ((_edgeA$getId = (_edgeA$getId2 = edgeA.getId) === null || _edgeA$getId2 === void 0 ? void 0 : _edgeA$getId2.call(edgeA)) !== null && _edgeA$getId !== void 0 ? _edgeA$getId : '').localeCompare((_edgeB$getId = (_edgeB$getId2 = edgeB.getId) === null || _edgeB$getId2 === void 0 ? void 0 : _edgeB$getId2.call(edgeB)) !== null && _edgeB$getId !== void 0 ? _edgeB$getId : '');
    });
    sortedEdges.forEach(edge => {
      var _edge$isGoToEdge;
      if ((_edge$isGoToEdge = edge.isGoToEdge) !== null && _edge$isGoToEdge !== void 0 && _edge$isGoToEdge.call(edge) || edge.getId() === edgeId) {
        const goToActionId = `${edge.getId()}-${SpecialActionIds.GO_TO}-${goToActionCounter++}`;
        if (edge.getId() === edgeId) {
          matchingId = goToActionId;
        }
        ids.push(goToActionId);
      }
    });
    return [ids, matchingId];
  }, [allEdges, edgeId]);
  const currentActionId = isNewAction ? matchingGoToActionId : edgeId;
  const actionIds = getActionIdsForGoToAction(flowAsGraph)(prevActionId);
  const initialActionCardProps = useMemo(() => {
    let propsMap = ImmutableMap();
    flow.getActionsAsList().forEach(action => {
      propsMap = propsMap.set(action.getActionId(), {
        disabled: !actionIds.includes(action.getActionId()),
        disabledDescription: 'automation.goToEditCanvas.disabledNodeTooltip',
        cardTheme: 'default'
      });
    });
    if (goToActionIds && goToActionIds.length > 0) {
      goToActionIds.forEach(action => {
        propsMap = propsMap.set(action, {
          disabled: true,
          disabledDescription: 'automation.goToEditCanvas.disabledNodeTooltipGoToAction',
          cardTheme: 'default',
          borderStyle: action === currentActionId ? 'selected' : undefined
        });
      });
    }
    return propsMap;
  }, [flow, actionIds, goToActionIds, currentActionId]);
  const [actionCardPropsById, setActionCardPropsById] = useState(initialActionCardProps);
  const updateActionCardProps = useCallback((id, props) => {
    setActionCardPropsById(prevProps => {
      const existingProps = prevProps.get(id) || {
        disabled: false,
        cardTheme: 'default'
      };
      return prevProps.set(id, Object.assign({}, existingProps, props));
    });
  }, []);
  const onPopoverCancel = useCallback(id => {
    updateActionCardProps(id, {
      renderConfirmationPopoverContent: undefined
    });
  }, [updateActionCardProps]);
  return {
    actionCardPropsById,
    updateActionCardProps,
    prevActionId,
    prevAction,
    getActionIdsForGoToAction,
    edgeId,
    onPopoverCancel,
    isNewAction,
    currentActionId,
    correctedEdgeId
  };
};
export default useGetGoToActionFlowData;